/**
 * 历史上的今天组件
 * 显示历史事件数据，带有优雅的加载动画
 */

import React, { useState, useEffect } from 'react';
import { historyApi } from '../../network/index.js';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * 历史上的今天组件
 * @param {Object} props - 组件属性
 * @param {string} props.className - 自定义样式类
 */
function TodayInHistory({ className = '' }) {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  /**
   * 获取今天的日期字符串
   * @returns {string} 格式化的日期字符串 "M/D"
   */
  const getTodayDateString = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    return `${month}/${day}`;
  };

  /**
   * 获取历史数据
   */
  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dateString = getTodayDateString();
      const data = await historyApi.getTodayInHistory(dateString);
      setHistoryData(data);
    } catch (err) {
      console.error('获取历史数据失败:', err);
      setError('获取历史数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 切换到下一个事件
   */
  const nextEvent = () => {
    if (historyData?.data?.Events) {
      setCurrentEventIndex((prev) => 
        (prev + 1) % historyData.data.Events.length
      );
    }
  };

  /**
   * 切换到上一个事件
   */
  const prevEvent = () => {
    if (historyData?.data?.Events) {
      setCurrentEventIndex((prev) => 
        prev === 0 ? historyData.data.Events.length - 1 : prev - 1
      );
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchHistoryData();
  }, []);

  // 自动轮播事件
  useEffect(() => {
    if (historyData?.data?.Events && historyData.data.Events.length > 1) {
      const interval = setInterval(nextEvent, 8000); // 8秒切换一次
      return () => clearInterval(interval);
    }
  }, [historyData]);

  if (loading) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-muted rounded animate-pulse"></div>
            <div className="h-3 bg-muted rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-3 text-destructive">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{error}</span>
          <button 
            onClick={fetchHistoryData}
            className="text-xs text-primary hover:text-primary/80 underline"
          >
            重试
          </button>
        </div>
      </motion.div>
    );
  }

  if (!historyData?.data?.Events || historyData.data.Events.length === 0) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center text-muted-foreground">
          <span className="text-sm">今天暂无历史事件数据</span>
        </div>
      </motion.div>
    );
  }

  const currentEvent = historyData.data.Events[currentEventIndex];
  const totalEvents = historyData.data.Events.length;

  return (
    <motion.div 
      className={`bg-card border border-border rounded-lg p-4 ${className}`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-sm font-semibold text-foreground">历史上的今天</h3>
          <span className="text-xs text-muted-foreground">({historyData.date})</span>
        </div>
        
        {totalEvents > 1 && (
          <div className="flex items-center space-x-2">
            <button 
              onClick={prevEvent}
              className="w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
              aria-label="上一个事件"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-muted-foreground">
              {currentEventIndex + 1}/{totalEvents}
            </span>
            <button 
              onClick={nextEvent}
              className="w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
              aria-label="下一个事件"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentEventIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="space-y-2"
        >
          <div className="flex items-start space-x-2">
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
              {currentEvent.year}
            </span>
            <p className="text-sm text-foreground leading-relaxed flex-1">
              {currentEvent.text}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* 进度指示器 */}
      {totalEvents > 1 && (
        <div className="flex space-x-1 mt-3 justify-center">
          {Array.from({ length: totalEvents }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentEventIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentEventIndex 
                  ? 'bg-primary' 
                  : 'bg-muted hover:bg-muted-foreground/30'
              }`}
              aria-label={`跳转到第 ${index + 1} 个事件`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default TodayInHistory;